import { useState, useEffect, useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer,
  AreaChart, Area, ScatterChart, Scatter
} from "recharts";

export default function ButtonPressTimeline() {
  const [events, setEvents] = useState([]);
  const [reportDefs, setReportDefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeGranularity, setTimeGranularity] = useState('day'); // hour, day, week
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [selectedSerials, setSelectedSerials] = useState([]);
  const [showScores, setShowScores] = useState(true);
  const [chartType, setChartType] = useState('line'); // line, area, scatter

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, reportsRes] = await Promise.all([
          fetch(process.env.REACT_APP_BACKEND_URL+"/button_press_data"),
          fetch(process.env.REACT_APP_BACKEND_URL+"/button_report_definitions")
        ]);

        const eventsData = await eventsRes.json();
        const reportsData = await reportsRes.json();

        setEvents(Array.isArray(eventsData) ? eventsData : []);
        setReportDefs(Array.isArray(reportsData) ? reportsData : []);
      } catch (err) {
        console.error("Failed to fetch timeline data:", err);
        setEvents([]);
        setReportDefs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Get unique serial numbers
  const uniqueSerials = useMemo(() => {
    return [...new Set(events.map(e => e.serial))].sort();
  }, [events]);

  // Initialize selected serials to all available
  useEffect(() => {
    if (uniqueSerials.length > 0 && selectedSerials.length === 0) {
      setSelectedSerials(uniqueSerials);
    }
  }, [uniqueSerials, selectedSerials]);

  // Helper function to format date based on granularity
  const formatDate = (timestamp, granularity) => {
    const date = new Date(timestamp * 1000);
    
    switch (granularity) {
      case 'hour':
        return date.toLocaleString('lv-LV', { 
          month: 'short', 
          day: 'numeric', 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      case 'day':
        return date.toLocaleDateString('lv-LV', { 
          month: 'short', 
          day: 'numeric' 
        });
      case 'week':
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        return `Nedēļa ${weekStart.toLocaleDateString('lv-LV', { month: 'short', day: 'numeric' })}`;
      default:
        return date.toLocaleDateString('lv-LV');
    }
  };

  // Helper function to group timestamps by granularity
  const groupByTimeGranularity = (timestamp, granularity) => {
    const date = new Date(timestamp * 1000);
    
    switch (granularity) {
      case 'hour':
        return Math.floor(timestamp / 3600) * 3600; // Round to hour
      case 'day':
        return Math.floor(timestamp / 86400) * 86400; // Round to day
      case 'week':
        const dayOfWeek = date.getDay();
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - dayOfWeek);
        weekStart.setHours(0, 0, 0, 0);
        return Math.floor(weekStart.getTime() / 1000);
      default:
        return Math.floor(timestamp / 86400) * 86400;
    }
  };

  // Process timeline data
  const timelineData = useMemo(() => {
    if (!events.length || selectedSerials.length === 0) return [];

    const startTimestamp = new Date(dateRange.startDate).getTime() / 1000;
    const endTimestamp = new Date(dateRange.endDate + 'T23:59:59').getTime() / 1000;

    // Filter events
    const filteredEvents = events.filter(event => 
      event.timestamp >= startTimestamp && 
      event.timestamp <= endTimestamp &&
      selectedSerials.includes(event.serial)
    );

    // Group by time period
    const groupedData = {};

    filteredEvents.forEach(event => {
      const timeKey = groupByTimeGranularity(event.timestamp, timeGranularity);
      const def = reportDefs.find(r => r.serial === event.serial && r.button_id === event.button_id);
      
      if (!def) return;

      if (!groupedData[timeKey]) {
        groupedData[timeKey] = {
          timestamp: timeKey,
          dateLabel: formatDate(timeKey, timeGranularity),
          totalEvents: 0,
          serviceScore: 0,
          good: 0,
          neutral: 0,
          bad: 0,
          cleanup: 0,
          supplies1: 0,
          supplies2: 0
        };
      }

      groupedData[timeKey].totalEvents += 1;

      if (def.report_type === 1) { // Service quality
        if (event.button_id === 1) {
          groupedData[timeKey].good += 1;
          groupedData[timeKey].serviceScore += 100;
        } else if (event.button_id === 2) {
          groupedData[timeKey].neutral += 1;
        } else if (event.button_id === 3) {
          groupedData[timeKey].bad += 1;
          groupedData[timeKey].serviceScore -= 100;
        }
      } else if (def.report_type === 2) { // Supplies/requests
        if (event.button_id === 1) {
          groupedData[timeKey].cleanup += 1;
        } else if (event.button_id === 2) {
          groupedData[timeKey].supplies1 += 1;
        } else if (event.button_id === 3) {
          groupedData[timeKey].supplies2 += 1;
        }
      }
    });

    return Object.values(groupedData).sort((a, b) => a.timestamp - b.timestamp);
  }, [events, reportDefs, selectedSerials, dateRange, timeGranularity]);

  // Custom tooltip for better data display
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
          <p className="font-semibold">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const handleSerialToggle = (serial) => {
    setSelectedSerials(prev => 
      prev.includes(serial) 
        ? prev.filter(s => s !== serial)
        : [...prev, serial]
    );
  };

  const renderChart = () => {
    if (!timelineData.length) {
      return (
        <div className="flex items-center justify-center h-64 text-gray-500">
          Nav datu atlasītajam periodam
        </div>
      );
    }

    const commonProps = {
      data: timelineData,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    switch (chartType) {
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="dateLabel" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {showScores ? (
              <Area type="monotone" dataKey="serviceScore" stackId="1" stroke="#8884d8" fill="#8884d8" name="Servisa rezultāts" />
            ) : (
              <>
                <Area type="monotone" dataKey="good" stackId="1" stroke="#10b981" fill="#10b981" name="Laba kvalitāte" />
                <Area type="monotone" dataKey="neutral" stackId="1" stroke="#f59e0b" fill="#f59e0b" name="Neitrāla kvalitāte" />
                <Area type="monotone" dataKey="bad" stackId="1" stroke="#ef4444" fill="#ef4444" name="Slikta kvalitāte" />
                <Area type="monotone" dataKey="cleanup" stackId="2" stroke="#3b82f6" fill="#3b82f6" name="Uzkopšana" />
                <Area type="monotone" dataKey="supplies1" stackId="2" stroke="#8b5cf6" fill="#8b5cf6" name="Piegādes 1" />
                <Area type="monotone" dataKey="supplies2" stackId="2" stroke="#06b6d4" fill="#06b6d4" name="Piegādes 2" />
              </>
            )}
          </AreaChart>
        );
      
      case 'scatter':
        return (
          <ScatterChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="dateLabel" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Scatter dataKey="serviceScore" fill="#8884d8" name="Servisa rezultāts" />
          </ScatterChart>
        );
      
      default: // line
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="dateLabel" />
            <YAxis />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            {showScores ? (
              <Line type="monotone" dataKey="serviceScore" stroke="#8884d8" strokeWidth={3} name="Servisa rezultāts" />
            ) : (
              <>
                <Line type="monotone" dataKey="good" stroke="#10b981" strokeWidth={2} name="Laba kvalitāte" />
                <Line type="monotone" dataKey="neutral" stroke="#f59e0b" strokeWidth={2} name="Neitrāla kvalitāte" />
                <Line type="monotone" dataKey="bad" stroke="#ef4444" strokeWidth={2} name="Slikta kvalitāte" />
                <Line type="monotone" dataKey="cleanup" stroke="#3b82f6" strokeWidth={2} name="Uzkopšana" />
                <Line type="monotone" dataKey="supplies1" stroke="#8b5cf6" strokeWidth={2} name="Piegādes 1" />
                <Line type="monotone" dataKey="supplies2" stroke="#06b6d4" strokeWidth={2} name="Piegādes 2" />
              </>
            )}
          </LineChart>
        );
    }
  };

  if (loading) return <p className="p-4">Datu ielāde...</p>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Detalizēts pogu nospiešanas laika grafiks</h1>
        
        {/* Controls */}
        <div className="bg-white p-6 rounded-lg shadow mb-6 space-y-4">
          {/* Date and time controls */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sākuma datums</label>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Beigu datums</label>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Laika periodisks</label>
              <select
                value={timeGranularity}
                onChange={(e) => setTimeGranularity(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="hour">Stundas</option>
                <option value="day">Dienas</option>
                <option value="week">Nedēļas</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Grafika tips</label>
              <select
                value={chartType}
                onChange={(e) => setChartType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="line">Līniju grafiks</option>
                <option value="area">Laukuma grafiks</option>
                <option value="scatter">Izkliedes grafiks</option>
              </select>
            </div>

            <div className="flex items-end">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={showScores}
                  onChange={(e) => setShowScores(e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm">Rādīt rezultātus</span>
              </label>
            </div>
          </div>

          {/* Serial number selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Sērijas numuri</label>
            <div className="flex flex-wrap gap-2">
              {uniqueSerials.map(serial => (
                <label key={serial} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedSerials.includes(serial)}
                    onChange={() => handleSerialToggle(serial)}
                    className="mr-1"
                  />
                  <span className="text-sm">{serial}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Datu punkti</h3>
            <p className="text-2xl font-bold text-gray-900">{timelineData.length}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Kopējie notikumi</h3>
            <p className="text-2xl font-bold text-gray-900">
              {timelineData.reduce((sum, item) => sum + item.totalEvents, 0)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Atlasītie sērijas numuri</h3>
            <p className="text-2xl font-bold text-gray-900">{selectedSerials.length}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Kopējais rezultāts</h3>
            <p className="text-2xl font-bold text-gray-900">
              {timelineData.reduce((sum, item) => sum + item.serviceScore, 0)}
            </p>
          </div>
        </div>

        {/* Chart */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-4">
            Pogu nospiešanas laika grafiks
            {showScores ? ' (Servisa kvalitātes rezultāti)' : ' (Sadalījums pa tipiem)'}
          </h3>
          <ResponsiveContainer width="100%" height={500}>
            {renderChart()}
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}